import { MAX_INPUT_LENGTH } from "#features/conversations/constants.ts";
import { fetchSpeechTokenWithRetry } from "#features/conversations/utils/fetchSpeechTokenWithRetry.ts";
import { findCodeBlocks } from "#features/conversations/utils/markdownCode.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useAiApiClient } from "#lib/AiApiClient.ts";
import type * as SpeechSdk from "microsoft-cognitiveservices-speech-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

export type DictationStatus = "idle" | "starting" | "listening" | "error";

// Product vocabulary the recognizer should favor over phonetically similar words.
const DICTATION_PHRASE_BOOSTS = ["Workleap", "Workleap AI", "Officevibe", "ShareGate", "Smart Report", "LMS", "one-on-one", "onboarding"];

// Reuse a cached token as long as it has comfortably more than a minute of life left.
const TOKEN_REUSE_SAFETY_WINDOW_MS = 60_000;

let speechSdkModule: Promise<typeof SpeechSdk> | null = null;

// The Speech SDK is heavy, so it is code-split and warmed once per page when dictation is available.
// Speech tokens are intentionally fetched only after the user starts dictation.
function loadSpeechSdk() {
    speechSdkModule ??= import("microsoft-cognitiveservices-speech-sdk");

    return speechSdkModule;
}

const endsWithClosedCodeBlock = (value: string) => {
    const lines = value.split("\n");
    const lastBlock = findCodeBlocks(lines).at(-1);

    return lastBlock !== undefined && lastBlock.isClosed && lastBlock.last === lines.length - 1;
};

// A space after a closing fence would turn it into a line that no longer closes the block, so speech starts below it.
const dictationBaseText = (currentValue: string) => {
    if (endsWithClosedCodeBlock(currentValue)) {
        return `${currentValue}\n`;
    }

    return currentValue.length > 0 && !currentValue.endsWith(" ") ? `${currentValue} ` : currentValue;
};

interface CachedToken {
    token: string;
    region: string;
    expiresAtMs: number;
}

interface UseDictationOptions {
    enabled: boolean;
    getCurrentValue: () => string;
    setValue: (value: string) => void;
}

export function useDictation({ enabled, getCurrentValue, setValue }: UseDictationOptions) {
    const aiApiClient = useAiApiClient();
    const { i18n } = useSharedTranslation();
    const [status, setStatus] = useState<DictationStatus>("idle");
    // The single microphone stream for the session — shared between the recognizer and the level meter,
    // so the mic is acquired exactly once (multiple captures wake every meeting app on the machine twice).
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);

    // Recognition follows the UI locale instead of auto-detecting between EN/FR: language identification
    // makes the service buffer audio to classify the language before the FIRST hypothesis, which users
    // perceive as "dictation takes forever to start writing". Locale-locked recognition starts instantly.
    const dictationLanguage = i18n.language?.startsWith("fr") ? "fr-CA" : "en-US";

    // Incremented on every start/stop: lets async continuations detect they belong to a cancelled session.
    const sessionIdRef = useRef(0);
    const sessionAbortControllerRef = useRef<AbortController | null>(null);
    const recognizerRef = useRef<SpeechSdk.SpeechRecognizer | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const tokenCacheRef = useRef<CachedToken | null>(null);
    // Text already in the input when dictation started, finalized segments since, and the last value we wrote.
    const baseTextRef = useRef("");
    const finalsTextRef = useRef("");
    const lastAppliedValueRef = useRef("");
    const tokenRefreshTimerRef = useRef<number | null>(null);

    const fetchToken = useCallback(async (signal?: AbortSignal) => {
        const cached = tokenCacheRef.current;
        if (cached && cached.expiresAtMs - Date.now() > TOKEN_REUSE_SAFETY_WINDOW_MS) {
            return cached;
        }

        const { token, region, expiresInSeconds } = await aiApiClient.getSpeechToken(signal);
        const fresh: CachedToken = { token, region, expiresAtMs: Date.now() + expiresInSeconds * 1000 };
        tokenCacheRef.current = fresh;

        return fresh;
    }, [aiApiClient]);

    const teardown = useCallback(() => {
        sessionIdRef.current += 1;
        sessionAbortControllerRef.current?.abort();
        sessionAbortControllerRef.current = null;

        if (tokenRefreshTimerRef.current !== null) {
            window.clearTimeout(tokenRefreshTimerRef.current);
            tokenRefreshTimerRef.current = null;
        }

        const recognizer = recognizerRef.current;
        recognizerRef.current = null;

        if (recognizer) {
            recognizer.stopContinuousRecognitionAsync(
                () => recognizer.close(),
                () => recognizer.close()
            );
        }

        const stream = mediaStreamRef.current;
        mediaStreamRef.current = null;
        stream?.getTracks().forEach(track => track.stop());
        setAudioStream(null);
    }, []);

    const stop = useCallback(() => {
        teardown();
        setStatus("idle");
    }, [teardown]);

    const applyTranscript = useCallback((interimText: string) => {
        // If the user edited the input since our last write, rebase on their text instead of clobbering it.
        const currentValue = getCurrentValue();
        if (currentValue !== lastAppliedValueRef.current) {
            baseTextRef.current = dictationBaseText(currentValue);
            finalsTextRef.current = "";
        }

        const nextValue = `${baseTextRef.current}${finalsTextRef.current}${interimText}`.slice(0, MAX_INPUT_LENGTH);
        lastAppliedValueRef.current = nextValue;
        setValue(nextValue);
    }, [getCurrentValue, setValue]);

    const start = useCallback(async () => {
        if (recognizerRef.current) {
            return;
        }

        const sessionId = ++sessionIdRef.current;
        const sessionAbortController = new AbortController();
        sessionAbortControllerRef.current?.abort();
        sessionAbortControllerRef.current = sessionAbortController;
        const startedAtMs = performance.now();
        let streamPromise: Promise<MediaStream> | null = null;
        setStatus("starting");

        try {
            // Microphone acquisition dominates startup latency (hardware + OS arbitration), so it runs
            // in parallel with the SDK load and token fetch instead of being paid serially at the end.
            streamPromise = navigator.mediaDevices.getUserMedia({ audio: true });
            const [stream, sdk, { token, region }] = await Promise.all([
                streamPromise,
                loadSpeechSdk(),
                fetchSpeechTokenWithRetry(fetchToken, sessionAbortController.signal)
            ]);

            if (sessionIdRef.current !== sessionId) {
                stream.getTracks().forEach(track => track.stop());

                return; // Stopped while starting up.
            }

            // eslint-disable-next-line no-console
            console.debug(`[dictation] mic + sdk + token ready in ${Math.round(performance.now() - startedAtMs)}ms`);

            mediaStreamRef.current = stream;
            setAudioStream(stream);

            const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(token, region);
            speechConfig.speechRecognitionLanguage = dictationLanguage;
            const audioConfig = sdk.AudioConfig.fromStreamInput(stream);
            const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

            sdk.PhraseListGrammar.fromRecognizer(recognizer).addPhrases(DICTATION_PHRASE_BOOSTS);

            const currentValue = getCurrentValue();
            baseTextRef.current = dictationBaseText(currentValue);
            finalsTextRef.current = "";
            lastAppliedValueRef.current = currentValue;

            // Only show "listening" once the service session is actually live — not when we merely asked it to start.
            const markListening = () => {
                if (recognizerRef.current === recognizer && sessionIdRef.current === sessionId) {
                    setStatus(previous => previous === "starting" ? "listening" : previous);
                }
            };

            recognizer.sessionStarted = () => {
                // eslint-disable-next-line no-console
                console.debug(`[dictation] session live in ${Math.round(performance.now() - startedAtMs)}ms`);
                markListening();
            };

            recognizer.recognizing = (_sender, event) => {
                if (recognizerRef.current !== recognizer) {
                    return;
                }

                markListening();

                if (event.result.text) {
                    applyTranscript(event.result.text);
                }
            };

            recognizer.recognized = (_sender, event) => {
                if (recognizerRef.current !== recognizer) {
                    return;
                }

                if (event.result.reason === sdk.ResultReason.RecognizedSpeech && event.result.text) {
                    finalsTextRef.current = `${finalsTextRef.current}${event.result.text} `;
                    applyTranscript("");
                }
            };

            recognizer.canceled = (_sender, event) => {
                if (recognizerRef.current !== recognizer) {
                    return;
                }

                if (event.reason === sdk.CancellationReason.Error) {
                    // eslint-disable-next-line no-console
                    console.error("[dictation] recognition canceled:", event.errorDetails);
                    teardown();
                    setStatus("error");
                }
            };

            recognizer.sessionStopped = () => {
                // Service-side end of session (network drop, etc.) — reflect it in the UI.
                if (recognizerRef.current === recognizer) {
                    teardown();
                    setStatus("idle");
                }
            };

            recognizerRef.current = recognizer;

            recognizer.startContinuousRecognitionAsync(
                undefined,
                error => {
                    if (sessionIdRef.current !== sessionId) {
                        return;
                    }

                    // eslint-disable-next-line no-console
                    console.error("[dictation] failed to start recognition:", error);
                    teardown();
                    setStatus("error");
                }
            );

            // Azure speech tokens expire after ~10 minutes: refresh shortly before so long sessions survive.
            const cachedToken = tokenCacheRef.current;
            const refreshDelayMs = Math.max((cachedToken ? cachedToken.expiresAtMs - Date.now() : 0) - TOKEN_REUSE_SAFETY_WINDOW_MS, 30_000);
            const refreshAuthorizationToken = async () => {
                if (sessionIdRef.current !== sessionId) {
                    return false;
                }

                try {
                    tokenCacheRef.current = null;
                    const refreshed = await fetchSpeechTokenWithRetry(fetchToken, sessionAbortController.signal);
                    const activeRecognizer = recognizerRef.current;
                    if (sessionIdRef.current !== sessionId || !activeRecognizer) {
                        return false;
                    }

                    activeRecognizer.authorizationToken = refreshed.token;

                    return true;
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("[dictation] token refresh failed after three retries:", error);

                    return false;
                }
            };
            const scheduleTokenRefresh = () => {
                tokenRefreshTimerRef.current = window.setTimeout(async () => {
                    if (await refreshAuthorizationToken() && sessionIdRef.current === sessionId) {
                        scheduleTokenRefresh();
                    }
                }, refreshDelayMs);
            };
            scheduleTokenRefresh();
        } catch (error) {
            void streamPromise?.then(stream => {
                stream.getTracks().forEach(track => track.stop());
            }, () => {});

            if (sessionIdRef.current !== sessionId) {
                return;
            }

            // eslint-disable-next-line no-console
            console.error("[dictation] could not start:", error);
            teardown();
            setStatus("error");
        }
    }, [applyTranscript, dictationLanguage, fetchToken, getCurrentValue, teardown]);

    const toggle = useCallback(() => {
        if (status === "listening" || status === "starting") {
            stop();
        } else {
            void start();
        }
    }, [status, start, stop]);

    // Warm only the SDK chunk. Fetching a token before the user clicks the microphone creates an
    // unnecessary cross-origin request on page load and turns transient staging failures into console noise.
    useEffect(() => {
        if (enabled) {
            void loadSpeechSdk();
        }
    }, [enabled]);

    // Release the microphone if the composer unmounts mid-dictation.
    useEffect(() => teardown, [teardown]);

    return { status, audioStream, toggle, stop };
}
